import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { exportXlsxConfig } from 'utils/general';
import { FrequencyUnit } from 'types/general';
import {
  IRecognitionAllowance,
  IRecognitionAllowanceDetail,
  IRecognitionAllowanceDetailsMeta,
  IRecognitionAllowanceRule,
} from 'types/income';
import {
  RecognitionAllowanceDetailsFilters,
  RecognitionAllowancesFilters,
} from './recognition-allowances.types';

interface GetAllowances {
  filters: Partial<RecognitionAllowancesFilters>;
}

interface GetAllowanceDetails {
  employeeId: string;
  filters: Partial<RecognitionAllowanceDetailsFilters>;
}

interface CreateAllowanceRule {
  points: number;
  frequency: FrequencyUnit;
  employeeGroups: string[];
  assignAllowanceOnCreation: boolean;
}

interface UpdateAllowanceRule {
  allowanceRuleId: string;
  data: Partial<Omit<CreateAllowanceRule, 'assignAllowanceOnCreation'>>;
}

interface RemoveAllowanceRule {
  allowanceRuleId: string;
}

export const loaders = {
  getAllowances: ({ filters }: GetAllowances) => {
    return api<{
      count: number;
      page: number;
      pageSize: number;
      employees: IRecognitionAllowance[];
    }>({
      url: Endpoints.clientAdmin.recognitions.allowances.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
      },
    });
  },
  getAllowanceDetails: ({ employeeId, filters }: GetAllowanceDetails) => {
    return api<
      {
        allowances: IRecognitionAllowanceDetail[];
      } & IRecognitionAllowanceDetailsMeta
    >({
      url: Endpoints.clientAdmin.recognitions.allowances.byEmployee.replace(
        ':employeeId',
        employeeId,
      ),
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
      },
    });
  },
  exportAllowancesXls: () => {
    return api({
      url: Endpoints.clientAdmin.recognitions.allowances.export.xls,
      ...exportXlsxConfig,
    });
  },
  getAllowanceRules: () => {
    return api<IRecognitionAllowanceRule[]>({
      url: Endpoints.clientAdmin.recognitions.allowances.rules.root,
    });
  },
  createAllowanceRule: ({ employeeGroups, ...data }: CreateAllowanceRule) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.recognitions.allowances.rules.root,
      data: {
        ...data,
        userGroupIds: employeeGroups,
      },
      method: 'post',
    });
  },
  updateAllowanceRule: ({
    allowanceRuleId,
    data: { employeeGroups, ...data },
  }: UpdateAllowanceRule) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.recognitions.allowances.rules.byId.replace(
        ':allowanceRuleId',
        allowanceRuleId,
      ),
      data: {
        ...data,
        userGroupIds: employeeGroups,
      },
      method: 'patch',
    });
  },
  removeAllowanceRule: ({ allowanceRuleId }: RemoveAllowanceRule) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.recognitions.allowances.rules.byId.replace(
        ':allowanceRuleId',
        allowanceRuleId,
      ),
      method: 'delete',
    });
  },
};
